/**
 * Single provider-agnostic authentication configuration (T008).
 *
 * Consolidates what were previously two parallel config systems into one AuthConfig,
 * derived entirely from the validated RuntimeConfig (config/index.ts).
 */

import { getConfig, getOidcClientConfig } from './index';
import type { OidcClientConfig, MockUser } from './types';
import type { AuthProviderType, OidcProviderConfig } from '../auth/providers/types';
import { getAuthProvider, buildOidcConfig, isMockAuthEnabled } from '../auth/providers/oidc';

/** The single authentication configuration used across the app. */
export interface AuthConfig {
  /** Active provider ('cognito' for real OIDC, 'local' for mock). */
  provider: AuthProviderType;
  /** Provider-agnostic OIDC config, or null in mock mode. */
  oidcConfig: OidcProviderConfig | null;
  /** oidc-client-ts client config, or null in mock mode. */
  oidcClientConfig: OidcClientConfig | null;
  /** Whether mock auth is enabled. */
  isMockAuth: boolean;
  /** Mock user seed (present in mock mode). */
  mockUser?: MockUser;
}

let _config: AuthConfig | null = null;

/** Load the authentication configuration from the validated runtime config. */
export function loadAuthConfig(): AuthConfig {
  const runtime = getConfig();
  return {
    provider: getAuthProvider(),
    oidcConfig: buildOidcConfig(),
    oidcClientConfig: getOidcClientConfig(),
    isMockAuth: isMockAuthEnabled(),
    mockUser: runtime.mockUser,
  };
}

/** Get the authentication configuration (singleton). */
export function getAuthConfig(): AuthConfig {
  if (_config === null) {
    _config = loadAuthConfig();
  }
  return _config;
}

export type { AuthProviderType, OidcProviderConfig };
